import React from 'react';

export type CameraStatus = 'off' | 'starting' | 'on' | 'denied' | 'unavailable';

/**
 * Local self-view only. This stream is separate from the audio-only stream used for the realtime
 * session; its tracks are never added to any peer connection.
 */
export function useSelfCamera(): {
  status: CameraStatus;
  stream: MediaStream | null;
  toggle: () => void;
} {
  const [status, setStatus] = React.useState<CameraStatus>('starting');
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const [wanted, setWanted] = React.useState(true);

  React.useEffect(() => {
    if (!wanted) {
      setStatus('off');
      return undefined;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setStatus('unavailable');
      return undefined;
    }
    let cancelled = false;
    let active: MediaStream | null = null;
    setStatus('starting');
    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      })
      .then((next) => {
        if (cancelled) {
          next.getTracks().forEach((track) => track.stop());
          return;
        }
        active = next;
        setStream(next);
        setStatus('on');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const name = error instanceof DOMException ? error.name : '';
        setStatus(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable');
      });
    return () => {
      cancelled = true;
      active?.getTracks().forEach((track) => track.stop());
      setStream(null);
    };
  }, [wanted]);

  const toggle = React.useCallback(() => setWanted((current) => !current), []);
  return { status, stream, toggle };
}
